import { FormEvent, useEffect, useRef, useState } from "react";
import { ArrowLeft, Copy, Eye, Mail, RefreshCw, RotateCcw, Save, Search, Send, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminUser,
  getCurrentAdmin,
  logout,
  AdminNewsletterCampaign,
  AdminNewsletterCampaignRecipient,
  NewsletterCampaignStatus,
  NewsletterRecipientCounts,
  PaginationMeta,
  getAdminNewsletterCampaign,
  listAdminNewsletterCampaignRecipients,
  listAdminNewsletterCampaigns,
  listAdminNewsletterSubscribers,
  processAdminNewsletterCampaignBatch,
  retryAdminNewsletterCampaignFailed,
  saveAdminNewsletterCampaign,
  sendAdminNewsletterCampaignTest,
  startAdminNewsletterCampaign,
  uploadAdminNewsletterCampaignImage,
} from "./api";
import { AdminRichTextEditor } from "./components/AdminRichTextEditor";
import { AdminFormNotice } from "./components/AdminFormNotice";
import { useConfirmDialog } from "./components/ConfirmDialog";
import Pagination from "./components/Pagination";

type FormState = {
  Subject: string;
  PreviewText: string;
  HtmlContent: string;
};

const emptyForm: FormState = { Subject: "", PreviewText: "", HtmlContent: "" };

const templateStatusLabels: Record<NewsletterCampaignStatus, string> = {
  Draft: "草稿",
  Sending: "寄送中",
  Sent: "已寄出",
};

function RequiredMark() {
  return <span className="ml-1 text-[#c3423f]" aria-hidden="true">*</span>;
}

function campaignToForm(item: AdminNewsletterCampaign): FormState {
  return {
    Subject: item.Subject,
    PreviewText: item.PreviewText || "",
    HtmlContent: item.HtmlContent,
  };
}

function NewsletterPreviewOverlay({ form, onClose }: { form: FormState; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[80] overflow-auto bg-[#f0f3f1]">
      <div className="sticky top-0 z-10 border-b border-[#dfe6e1] bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#9a8a7a]">電子報預覽</div>
            <h2 className="text-xl font-bold text-[#1B3D2A]">{form.Subject || "未命名電子報"}</h2>
          </div>
          <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={onClose}>
            <X size={18} />
            關閉預覽
          </button>
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-4 rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-sm text-[#59665f]">
          <p>寄件人：富聆聽健康 &lt;no-reply@fulling-hearing.tw&gt;</p>
          <p>主旨：{form.Subject || "（尚未填寫主旨）"}</p>
          {form.PreviewText && <p className="text-[#9a8a7a]">預覽文字：{form.PreviewText}</p>}
        </div>
        <div className="rounded-md border border-[#dfe6e1] bg-white p-6">
          <div
            className="prose max-w-none text-[#1B3D2A] prose-headings:text-[#1B3D2A] prose-a:text-[#245438] prose-img:rounded-md prose-iframe:aspect-video prose-iframe:w-full prose-iframe:rounded-md"
            dangerouslySetInnerHTML={{ __html: form.HtmlContent || "<p>尚未撰寫電子報內容。</p>" }}
          />
          <hr className="my-6 border-[#E8E0D6]" />
          <p className="text-xs text-[#9a8a7a]">您收到這封信是因為您曾在富聆聽健康網站訂閱電子報。若不想再收到此類信件，可以點此取消訂閱。（實際寄出的信件會附上專屬的取消訂閱連結）</p>
        </div>
      </main>
    </div>
  );
}

const recipientStatusLabels: Record<string, string> = { Pending: "待寄送", Sent: "已寄出", Failed: "失敗" };
const recipientStatusClasses: Record<string, string> = {
  Pending: "bg-[#f0f3f1] text-[#59665f]",
  Sent: "bg-[#e9f4ec] text-[#245438]",
  Failed: "bg-[#fdebea] text-[#9a2d2d]",
};

const defaultRecipientPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

/** 電子報「寄送紀錄」明細：分頁列出這封電子報底下每一位收件人的個別寄送狀態，讓管理者可以查詢單一信箱是否已收到、失敗原因為何。 */
function RecipientLogSection({ campaignId }: { campaignId: string }) {
  const [items, setItems] = useState<AdminNewsletterCampaignRecipient[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>(defaultRecipientPagination);
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  async function load(filters = { Status: status, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setLoadError("");
    try {
      const data = await listAdminNewsletterCampaignRecipients(campaignId, filters);
      setItems(data.Items);
      setPagination(data.Pagination);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "寄送紀錄載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Keyword: "", Page: 1, PerPage: defaultRecipientPagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  return (
    <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#203d32]">寄送紀錄明細</h2>
          <p className="mt-1 text-sm text-[#59665f]">每一位收件人的個別寄送狀態都會保留下來，可依信箱或狀態查詢。</p>
        </div>
        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
          <RefreshCw size={16} />
          重新整理
        </button>
      </div>

      <form className="mb-4 flex flex-wrap items-end gap-3" onSubmit={handleSearch}>
        <div className="grid min-w-[120px] gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="recipient-status">狀態</label>
          <select id="recipient-status" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Pending">待寄送</option>
            <option value="Sent">已寄出</option>
            <option value="Failed">失敗</option>
          </select>
        </div>
        <div className="grid min-w-[220px] flex-1 gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="recipient-keyword">Email 關鍵字</label>
          <input id="recipient-keyword" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="搜尋收件人信箱" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
      </form>

      {loadError && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{loadError}</div>}

      <div className="rounded-md border border-[#edf0ee]">
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full min-w-[640px] table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[280px] px-4 py-3">收件人信箱</th>
                <th className="w-[100px] px-4 py-3">狀態</th>
                <th className="w-[160px] px-4 py-3">寄送時間</th>
                <th className="px-4 py-3">失敗原因</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-8 text-center text-[#59665f]" colSpan={4}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-8 text-center text-[#59665f]" colSpan={4}>目前沒有符合條件的收件紀錄。</td></tr>
              ) : items.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.RecipientId}>
                  <td className="truncate px-4 py-3 text-sm text-[#203d32]">{item.Email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${recipientStatusClasses[item.Status] || "bg-[#f0f3f1] text-[#59665f]"}`}>
                      {recipientStatusLabels[item.Status] || item.Status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#59665f]">{item.SentAt || "-"}</td>
                  <td className="px-4 py-3 text-sm text-[#9a2d2d]">{item.ErrorMessage || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>
    </section>
  );
}

export default function AdminNewsletterCampaignForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const campaignId = id || null;
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [campaign, setCampaign] = useState<AdminNewsletterCampaign | null>(null);
  const [recipientCounts, setRecipientCounts] = useState<NewsletterRecipientCounts>({ Pending: 0, Sent: 0, Failed: 0 });
  const [activeSubscriberCount, setActiveSubscriberCount] = useState(0);
  const [loading, setLoading] = useState(Boolean(campaignId));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testMessage, setTestMessage] = useState("");
  const [batchRunning, setBatchRunning] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [templateOptions, setTemplateOptions] = useState<AdminNewsletterCampaign[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const stopRequestedRef = useRef(false);

  async function init() {
    setLoading(Boolean(campaignId));
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const subscribers = await listAdminNewsletterSubscribers({ Status: "Active", Page: 1, PerPage: 1 });
      setActiveSubscriberCount(subscribers.Counts?.Active ?? subscribers.Pagination.Total);
      const pastCampaigns = await listAdminNewsletterCampaigns({ Page: 1, PerPage: 50 });
      setTemplateOptions(pastCampaigns.Items.filter((item) => item.CampaignId !== campaignId));
      if (campaignId) {
        const data = await getAdminNewsletterCampaign(campaignId);
        setCampaign(data.Item);
        setForm(campaignToForm(data.Item));
        setRecipientCounts(data.RecipientCounts);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "電子報資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function setField<Key extends keyof FormState>(key: Key, value: FormState[Key]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function validate() {
    const errors: string[] = [];
    if (!form.Subject.trim()) errors.push("請輸入電子報主旨。");
    if (!form.HtmlContent.replace(/<[^>]*>/g, "").trim()) errors.push("請輸入電子報內容。");
    return errors;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const errors = validate();
    if (errors.length > 0) {
      setMessage(errors[0]);
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const saved = await saveAdminNewsletterCampaign(campaignId, {
        Subject: form.Subject.trim(),
        PreviewText: form.PreviewText.trim() || undefined,
        HtmlContent: form.HtmlContent,
      });
      setCampaign(saved.Item);
      setMessage("電子報已儲存為草稿。");
      if (!campaignId) {
        navigate(`/admin/newsletter-campaigns/${saved.Item.CampaignId}/edit`, { replace: true });
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "電子報儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleApplyTemplate() {
    const template = templateOptions.find((option) => option.CampaignId === selectedTemplateId);
    if (!template) return;

    const confirmed = await confirm({
      title: "套用電子報內容？",
      description: (
        <>
          將用 <strong className="font-bold text-[#203d32]">{template.Subject}</strong> 的主旨與內容取代目前編輯中的草稿。
          <br />
          原本那份電子報不會被更動，但目前尚未儲存的修改內容會被覆蓋。
        </>
      ),
      confirmText: "套用內容",
      cancelText: "取消",
      tone: "warning",
    });
    if (!confirmed) return;

    setForm(campaignToForm(template));
    setSelectedTemplateId("");
    setMessage(`已套用「${template.Subject}」的內容，請修改後再儲存。`);
  }

  async function handleSendTest() {
    if (!campaignId) {
      setTestMessage("請先儲存草稿後再寄送測試信。");
      return;
    }
    const email = testEmail.trim();
    if (!email) {
      setTestMessage("請輸入測試信箱。");
      return;
    }
    setTestSending(true);
    setTestMessage("");
    try {
      await sendAdminNewsletterCampaignTest(campaignId, email);
      setTestMessage(`測試信已送出至 ${email}。`);
    } catch (error) {
      setTestMessage(error instanceof Error ? error.message : "測試信寄送失敗。");
    } finally {
      setTestSending(false);
    }
  }

  async function runSendLoop(id: string) {
    stopRequestedRef.current = false;
    setBatchRunning(true);
    try {
      let done = false;
      while (!done && !stopRequestedRef.current) {
        const result = await processAdminNewsletterCampaignBatch(id);
        setCampaign(result.Item);
        setRecipientCounts(result.RecipientCounts);
        done = result.Done;
        if (!done) {
          await new Promise((resolve) => window.setTimeout(resolve, 250));
        }
      }
      setMessage(done ? "電子報已全部寄送完成。" : "已停止寄送，可稍後再繼續。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "寄送過程發生錯誤，請確認後重試。");
    } finally {
      setBatchRunning(false);
    }
  }

  async function handleStartSending() {
    if (!campaignId || !campaign) return;
    const confirmed = await confirm({
      title: "開始寄送電子報？",
      description: (
        <>
          即將寄送給目前 <strong className="font-bold text-[#203d32]">{activeSubscriberCount}</strong> 位訂閱中的會員。
          <br />
          開始寄送後內容將無法再編輯，此動作無法復原，請確認內容無誤。
        </>
      ),
      confirmText: "開始寄送",
      cancelText: "再檢查一下",
      tone: "warning",
    });
    if (!confirmed) return;

    setMessage("");
    try {
      const started = await startAdminNewsletterCampaign(campaignId);
      setCampaign(started.Item);
      await runSendLoop(campaignId);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "開始寄送失敗，請稍後再試。");
    }
  }

  async function handleContinueSending() {
    if (!campaignId) return;
    await runSendLoop(campaignId);
  }

  async function handleRetryFailed() {
    if (!campaignId) return;
    setMessage("");
    try {
      const result = await retryAdminNewsletterCampaignFailed(campaignId);
      setCampaign(result.Item);
      await runSendLoop(campaignId);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "重寄失敗名單時發生錯誤。");
    }
  }

  const isDraft = !campaign || campaign.Status === "Draft";
  const isSending = campaign?.Status === "Sending";
  const isSent = campaign?.Status === "Sent";
  const progressTotal = campaign?.TotalRecipients || 0;
  const progressDone = (campaign?.SentCount || 0) + (campaign?.FailedCount || 0);
  const progressPercent = progressTotal > 0 ? Math.round((progressDone / progressTotal) * 100) : 0;

  return (
    <AdminLayout sectionLabel="內容管理" title={campaignId ? "編輯電子報" : "新增電子報"} user={currentUser} onLogout={handleLogout}>
      <form onSubmit={handleSubmit}>
        <div className="sticky top-[76px] z-10 mb-5 rounded-md border border-[#dfe6e1] bg-white/95 p-3 shadow-[0_10px_24px_rgba(32,61,50,.08)] backdrop-blur md:top-[110px]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => navigate("/admin/newsletter-campaigns")} type="button">
              <ArrowLeft size={18} />
              回電子報列表
            </button>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => setPreviewOpen(true)} type="button">
                <Eye size={18} />
                預覽
              </button>
              {isDraft && (
                <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving || loading} type="submit">
                  <Save size={18} />
                  {saving ? "儲存中" : "儲存草稿"}
                </button>
              )}
            </div>
          </div>
        </div>

        <AdminFormNotice message={message} />

        {loading ? (
          <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-10 text-center text-[#59665f]">載入中...</div>
        ) : (
          <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="space-y-5">
              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">基本資訊</h2>
                <div className="grid gap-4">
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="campaign-subject">電子報主旨<RequiredMark /></label>
                    <input
                      id="campaign-subject"
                      className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15 disabled:bg-[#f7f9f8] disabled:text-[#9a8a7a]"
                      disabled={!isDraft}
                      value={form.Subject}
                      onChange={(event) => setField("Subject", event.target.value)}
                      placeholder="例如：富聆聽健康 8 月優惠活動搶先看"
                    />
                  </div>
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="campaign-preview">預覽文字</label>
                    <input
                      id="campaign-preview"
                      className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15 disabled:bg-[#f7f9f8] disabled:text-[#9a8a7a]"
                      disabled={!isDraft}
                      value={form.PreviewText}
                      onChange={(event) => setField("PreviewText", event.target.value)}
                      placeholder="部分信箱會顯示在主旨旁的摘要文字（選填）"
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">電子報內容<RequiredMark /></h2>
                {isDraft ? (
                  <AdminRichTextEditor value={form.HtmlContent} onChange={(value) => setField("HtmlContent", value)} onUploadImage={uploadAdminNewsletterCampaignImage} imageAlt="電子報內容圖片" />
                ) : (
                  <div className="prose max-w-none rounded-md border border-[#edf0ee] bg-[#f7f9f8] p-4 text-sm text-[#34443b]" dangerouslySetInnerHTML={{ __html: form.HtmlContent }} />
                )}
              </section>
            </div>

            <aside className="space-y-5">
              {isDraft && templateOptions.length > 0 && (
                <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                  <h2 className="mb-3 text-lg font-bold text-[#203d32]">套用過去電子報內容</h2>
                  <p className="mb-3 text-sm text-[#59665f]">可以選一份之前寄過或還在草稿中的電子報，把主旨與內容帶進來當作範本快速修改。</p>
                  <div className="grid gap-2">
                    <select
                      className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                      value={selectedTemplateId}
                      onChange={(event) => setSelectedTemplateId(event.target.value)}
                    >
                      <option value="">請選擇電子報...</option>
                      {templateOptions.map((option) => (
                        <option key={option.CampaignId} value={option.CampaignId}>
                          {templateStatusLabels[option.Status]}｜{option.Subject}
                        </option>
                      ))}
                    </select>
                    <button
                      className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={!selectedTemplateId}
                      type="button"
                      onClick={handleApplyTemplate}
                    >
                      <Copy size={16} />
                      套用這份內容
                    </button>
                  </div>
                </section>
              )}

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-3 text-lg font-bold text-[#203d32]">寄送測試信</h2>
                <p className="mb-3 text-sm text-[#59665f]">寄送前建議先寄一封測試信給自己確認排版與內容。</p>
                <div className="grid gap-2">
                  <input
                    className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                    placeholder="test@example.com"
                    type="email"
                    value={testEmail}
                    onChange={(event) => setTestEmail(event.target.value)}
                  />
                  <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60" disabled={testSending || !campaignId} type="button" onClick={handleSendTest}>
                    <Mail size={16} />
                    {testSending ? "寄送中..." : "寄送測試信"}
                  </button>
                  {!campaignId && <p className="text-xs text-[#9a8a7a]">請先儲存草稿後才能寄送測試信。</p>}
                  {testMessage && <p className="text-xs font-medium text-[#245438]">{testMessage}</p>}
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-3 text-lg font-bold text-[#203d32]">正式寄送</h2>
                <p className="mb-3 text-sm text-[#59665f]">
                  目前訂閱中收件人：<strong className="font-bold text-[#203d32]">{activeSubscriberCount}</strong> 位
                </p>

                {isDraft && (
                  <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={!campaignId || batchRunning} type="button" onClick={handleStartSending}>
                    <Send size={16} />
                    開始寄送
                  </button>
                )}
                {!campaignId && isDraft && <p className="mt-2 text-xs text-[#9a8a7a]">請先儲存草稿後才能開始寄送。</p>}

                {(isSending || isSent) && (
                  <div className="grid gap-3">
                    <div>
                      <div className="mb-1 flex items-center justify-between text-xs font-bold text-[#59665f]">
                        <span>寄送進度</span>
                        <span>{progressDone} / {progressTotal}（{progressPercent}%）</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-[#edf0ee]">
                        <div className="h-full rounded-full bg-[#315746] transition-all" style={{ width: `${progressPercent}%` }} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="rounded-md bg-[#f7f9f8] p-2">
                        <p className="font-bold text-[#59665f]">待寄送</p>
                        <p className="mt-1 text-lg font-bold text-[#203d32]">{recipientCounts.Pending}</p>
                      </div>
                      <div className="rounded-md bg-[#e9f4ec] p-2">
                        <p className="font-bold text-[#245438]">已寄出</p>
                        <p className="mt-1 text-lg font-bold text-[#245438]">{recipientCounts.Sent}</p>
                      </div>
                      <div className="rounded-md bg-[#fdebea] p-2">
                        <p className="font-bold text-[#9a2d2d]">失敗</p>
                        <p className="mt-1 text-lg font-bold text-[#9a2d2d]">{recipientCounts.Failed}</p>
                      </div>
                    </div>

                    {isSending && recipientCounts.Pending > 0 && (
                      <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={batchRunning} type="button" onClick={handleContinueSending}>
                        <RefreshCw size={16} className={batchRunning ? "animate-spin" : undefined} />
                        {batchRunning ? "寄送中..." : "繼續寄送"}
                      </button>
                    )}

                    {recipientCounts.Failed > 0 && !batchRunning && (
                      <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" type="button" onClick={handleRetryFailed}>
                        <RotateCcw size={16} />
                        重寄失敗名單（{recipientCounts.Failed}）
                      </button>
                    )}

                    {isSent && recipientCounts.Pending === 0 && recipientCounts.Failed === 0 && (
                      <p className="rounded-md bg-[#e9f4ec] px-3 py-2 text-center text-sm font-bold text-[#245438]">已全部寄送完成</p>
                    )}
                  </div>
                )}
              </section>
            </aside>
          </div>
        )}

        {!loading && campaignId && (isSending || isSent) && (
          <div className="mt-5">
            <RecipientLogSection campaignId={campaignId} />
          </div>
        )}
      </form>
      {previewOpen && <NewsletterPreviewOverlay form={form} onClose={() => setPreviewOpen(false)} />}
      <ConfirmDialog />
    </AdminLayout>
  );
}
